import { useState, useEffect, useMemo, useContext } from "react";
import SettingsContext from "@serie3/common/auth/SettingsContext";
import NotFoundPage from "../../../shared/404";
import { Row, Col } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import crudApi from "@serie3/common/api/crudApi";
import SocialEndpoints from "@serie3/mymediaplanner/Modules/Social/endpoints";
import KanbanBoard from "../../../shared/KanbanBoard/Kanbanboard";
import TelepromterModal from "../TelepromterModal";
import PostFormView from "../PostFormView";
import JobItem from "./JobItem";
import JobDetailModal from "./JobDetailModal";
import { groupJobsIntoColumns } from "./jobStatus";

const Stat = ({ value, label, color }) => (
  <Col md={3}>
    <div className="card">
      <div className="card-body">
        <h4 className="mb-0" style={{ color }}>
          {value}
        </h4>
        <div className="text-muted font-size-12">{label}</div>
      </div>
    </div>
  </Col>
);

/**
 * Jobs board — a monitoring view onto the Content Machine's queue.
 *
 * The machine owns every field, `status` included, so the board is read-only:
 * cards can be inspected and can launch the teleprompter on their linked post,
 * but dragging never writes a status back.
 *
 * `basePath` is where the board is mounted; the open job is appended to it
 * (`${basePath}/:jobKey`) so the board can live under any route.
 */
const JobsBoardInner = ({ basePath }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const [detailJob, setDetailJob] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const [teleprompterPost, setTeleprompterPost] = useState(null);
  const [showTeleprompter, setShowTeleprompter] = useState(false);

  // PostFormView edits against a list, so the fetched post gets its own
  // one-element list rather than being wedged into the jobs state.
  const [editPost, setEditPost] = useState(null);
  const [editPostList, setEditPostList] = useState([]);
  const [showEditPost, setShowEditPost] = useState(false);
  const [editTrigger, setEditTrigger] = useState(false);

  const navigate = useNavigate();
  // :jobKey deep link — the machine's long job id, e.g.
  // job-20260715-212334-de76. The numeric database id works too.
  const { jobKey } = useParams();

  useEffect(() => {
    const loadJobs = async () => {
      // getAll leaves state untouched and stays silent on failure, so check the
      // response ourselves to tell "no jobs" apart from "request broke".
      const response = await crudApi.getAll(
        SocialEndpoints.jobs.index,
        setJobs,
        SocialEndpoints.jobs.dataField,
      );
      setLoadFailed(!response?.ok);
      setLoading(false);
    };

    loadJobs();
  }, []);

  const columns = useMemo(() => groupJobsIntoColumns(jobs), [jobs]);

  const stats = useMemo(() => {
    const count = (status) => jobs.filter((job) => job.status === status).length;
    return {
      total: jobs.length,
      toScript: count("downloaded"),
      toFilm: count("scripted"),
      attention: count("error"),
    };
  }, [jobs]);

  // The URL is the source of truth for which job is open, so a deep link and a
  // card click end up in exactly the same place.
  const openDetails = (job) => {
    navigate(`${basePath}/${job.external_id ?? job.id}`);
  };

  const closeDetails = () => {
    setShowDetail(false);
    if (jobKey) navigate(basePath);
  };

  useEffect(() => {
    if (!jobKey) {
      setShowDetail(false);
      setDetailJob(null);
      return;
    }
    if (!jobs.length) return; // wait for the list before deciding it is missing

    const match = jobs.find(
      (job) => job.external_id === jobKey || String(job.id) === jobKey,
    );
    setDetailJob(match ?? null);
    setShowDetail(Boolean(match));
  }, [jobKey, jobs]);

  const deepLinkMissing =
    Boolean(jobKey) && !loading && jobs.length > 0 && !detailJob;

  // Leaves the job behind and hands over to the normal post editor.
  const openLinkedPost = async (postId) => {
    if (!postId) return;
    const post = await crudApi.show(SocialEndpoints.posts.show, postId);
    if (!post) return;
    closeDetails(); // also drops the job out of the URL
    setEditPost(post);
    setEditPostList([post]);
    setShowEditPost(true);
  };

  const openLinkedGroup = (groupId) => {
    if (!groupId) return;
    setShowDetail(false);
    navigate(`/groups/${groupId}`);
  };

  // Deletes the mirror in mymediaplanner only — the job folder on the machine
  // stays put, and a later bridge push will recreate the card.
  const deleteJob = async (job) => {
    if (!job?.id) return;
    const label = job.title || job.external_id || "this job";
    if (!window.confirm(`Remove "${label}" from the board?`)) return;

    const response = await crudApi.del(SocialEndpoints.jobs.delete, job.id);
    if (!response?.ok) return; // crudApi already surfaced the error
    setJobs((prev) => prev.filter((entry) => entry.id !== job.id));
  };

  // The script text lives on the linked post, not the job — fetch it, then hand
  // it to the existing post teleprompter unchanged.
  const openTeleprompter = async (postId) => {
    if (!postId) return;
    const post = await crudApi.show(SocialEndpoints.posts.show, postId);
    if (!post) return;
    closeDetails(); // avoid stacking on top of the job modal
    setTeleprompterPost(post);
    setShowTeleprompter(true);
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Row className="mb-3">
          <Col>
            <h4 className="mb-1">Jobs</h4>
            <div className="text-muted font-size-13">
              Everything the autoprocessor pulled in — from link to
              ready-to-film.
            </div>
          </Col>
        </Row>

        <Row className="mb-3">
          <Stat value={stats.total} label="Total jobs" />
          <Stat value={stats.toScript} label="Ready to script" color="#556ee6" />
          <Stat value={stats.toFilm} label="Scripted · to film" color="#f1b44c" />
          <Stat value={stats.attention} label="Need attention" color="#f46a6a" />
        </Row>

        {deepLinkMissing && (
          <div className="alert alert-warning">
            No job on this board matches <code>{jobKey}</code>. It may have been
            deleted, or it belongs to a different account.
          </div>
        )}

        {loadFailed && (
          <div className="alert alert-warning">
            Could not load jobs. The Content Machine API did not respond.
          </div>
        )}

        {loading ? (
          <div className="text-muted">Loading jobs…</div>
        ) : (
          <KanbanBoard
            columns={columns}
            getId={(job) => job.id ?? job.external_id}
            columnWidthPct={16}
            columnMaxWidthPx={280}
            gapPx={12}
            // Status is machine-owned; the board mirrors it and never writes back.
            dragDisabled
            onMove={() => {}}
            renderItem={({ item }) => (
              <JobItem
                job={item}
                onOpenDetails={openDetails}
                onTeleprompter={(job) => openTeleprompter(job.post_id)}
                onDelete={deleteJob}
              />
            )}
          />
        )}
      </div>

      <JobDetailModal
        show={showDetail}
        setShow={(value) => (value ? setShowDetail(true) : closeDetails())}
        job={detailJob}
        onOpenPost={openLinkedPost}
        onOpenTeleprompter={openTeleprompter}
        onOpenGroup={openLinkedGroup}
      />

      {editPost && (
        <PostFormView
          showEdit={showEditPost}
          setShowEdit={setShowEditPost}
          dataList={editPostList}
          setDataList={setEditPostList}
          editData={editPost}
          setEditData={setEditPost}
          trigger={editTrigger}
          setTrigger={setEditTrigger}
        />
      )}

      <TelepromterModal
        show={showTeleprompter}
        setShow={setShowTeleprompter}
        post={teleprompterPost}
      />
    </div>
  );
};

/**
 * Super-admin gate.
 *
 * The board is wrapped rather than guarded inline so a non-admin never mounts
 * the inner component — no jobs request goes out at all. Roles arrive async, so
 * render nothing until settings land, otherwise a permitted user would see a
 * 404 flash on their way in.
 *
 * This hides the UI only; the API must enforce the same rule server-side.
 */
const JobsBoard = ({ basePath = "/jobs" }) => {
  const { settings } = useContext(SettingsContext);

  if (!settings?.user?.roles) return null;

  const roles = Object.values(settings.user.roles);
  if (!roles.includes("ROLE_SUPER_ADMIN")) return <NotFoundPage />;

  return <JobsBoardInner basePath={basePath} />;
};

export default JobsBoard;
